import { CustomSelectOptionBaseType, CustomSelectOptionType, generateUniqueId, SelectChangeEventArgsType, SelectOptionType } from '@danfoss/mosaic-core';
import { DfSelectCustomEvent } from '@danfoss/mosaic-core/loader';
import { DfSelect } from '@danfoss/mosaic-react';
import { useCallback } from 'react';
import { BaseUIType } from '../../models/UITypes';
import { isNumber } from '../../helpers/typeHelpers';
import { withUIState } from '../../contexts/ui-state/withUIState';

export type SelectEventType = DfSelectCustomEvent<SelectChangeEventArgsType<false, false>>;

export type SelectProps = {
    className?: string;
    placeholder?: string;
    selectedOptionIndex?: number;
    options: CustomSelectOptionType[];
    onChange: (value?: string | string[] | CustomSelectOptionBaseType | CustomSelectOptionBaseType[]) => void;
    value?: string | string[];
    isDisabled?: boolean;
    isReadonly?: boolean;
    isLoading?: boolean;
    showSelectedOptions?: boolean;
    isSearchable?: boolean;
    label?: string;
    getOptionLabel?: (option: CustomSelectOptionType) => string;
};

const MultiSelect = ({
    testId,
    className,
    placeholder,
    value,
    isDisabled,
    isReadonly,
    isLoading,
    selectedOptionIndex,
    showSelectedOptions = false,
    options,
    onChange,
    isSearchable = false,
    label,
    getOptionLabel
}: BaseUIType & SelectProps) => {
    const handleChange = useCallback((e: SelectEventType) => {
        onChange(e.detail.value);
    }, [onChange]);

    return (
        <fieldset className="df-form-field" data-testid={testId}>
            {label && <label className="df-field-label">{label}</label>}
            <DfSelect
                className={className}
                placeholder={placeholder}
                options={options}
                isReadonly={isReadonly}
                aria-readonly={isReadonly}
                data-testid={testId}
                isMulti={true}
                isSearchable={isSearchable}
                disabled={isReadonly || isDisabled || isLoading}
                showSelectedOptions={showSelectedOptions}
                value={value}
                defaultSelectValue={isNumber(selectedOptionIndex) ? options?.[selectedOptionIndex] : undefined}
                onSelectChange={handleChange}
                getOptionLabel={getOptionLabel}
            />
        </fieldset>
    );
};

export const SelectStatic = withUIState(({
    label,
    testId,
    value,
    options,
    addEmptyPlaceholder,
    isDisabled,
    isLoading,
    isReadonly,
    onChange,
    isMultiChoice
}: {
    isReadonly?: boolean;
    label?: string;
    testId?: string;
    value?: string | string[];
    isDisabled?: boolean;
    isLoading?: boolean;
    options: SelectOptionType[];
    addEmptyPlaceholder?: string;
    onChange: (value: string | string[]) => void;
    isMultiChoice?: boolean;
}) => {
    const id = generateUniqueId();
    return (
        <fieldset className="df-form-field" data-testid={testId}>
            {label && <label className="df-field-label" htmlFor={id}>{label}</label>}
            <select
                multiple={!!isMultiChoice}
                disabled={isDisabled || isLoading || isReadonly}
                value={value ?? (isMultiChoice ? [] : '')}
                id={id}
                className="df-select"
                onChange={e => {
                    const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
                    onChange(selected);

                }}
            >
                {addEmptyPlaceholder !== undefined && !isMultiChoice && (
                    <option key={'initial'} value={''}>{addEmptyPlaceholder}</option>
                )}
                {options.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                ))}
            </select>
        </fieldset>
    );
});

export default withUIState(MultiSelect);
